import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FileText, Download, Check, Loader2, BellRing, Sparkles, ArrowRight, BookOpen, X } from 'lucide-react';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/Reveal';
import { useToast } from '../hooks/use-toast';
import { REPORTS as MOCK_REPORTS } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const META = MOCK_REPORTS.reduce((acc, r) => ({ ...acc, [r.id]: r }), {});

export default function Reports() {
  const { toast } = useToast();
  const [reports, setReports] = useState(MOCK_REPORTS);
  const [reading, setReading] = useState(null);
  const [brief, setBrief] = useState({ name: '', email: '', organisation: '', details: '' });
  const [savingBrief, setSavingBrief] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/reports`)
      .then((res) => setReports(res.data.map((r) => ({ ...META[r.id], ...r }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    document.body.style.overflow = reading ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [reading]);

  const fileUrl = (id) => `${API}/reports/file/${id}`;
  const downloadUrl = (id) => `${API}/reports/file/${id}?download=1`;

  const download = (report) => {
    toast({ title: 'Preparing your download', description: `“${report.title}” — enjoy the read.` });
    window.open(downloadUrl(report.id), '_blank');
  };

  const submitBrief = async (e) => {
    e.preventDefault();
    if (!brief.name || !brief.email || !brief.details) {
      toast({ title: 'Please complete the required fields', description: 'Name, email and your research needs are required.' });
      return;
    }
    setSavingBrief(true);
    try {
      await axios.post(`${API}/research-brief`, brief);
      toast({ title: 'Brief received ✓', description: 'Thank you — our consultants will assess your request and be in touch.' });
      setBrief({ name: '', email: '', organisation: '', details: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again shortly.' });
    } finally {
      setSavingBrief(false);
    }
  };

  return (
    <div>
      <PageBanner
        title="Our Reports"
        subtitle="A library of EIG intelligence on the UAE education sector — examples of the work we produce when commissioned. Read online or download, free."
        crumb="Reports"
        image="https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMGFuYWx5dGljc3xlbnwwfHx8fDE3OTA4NDkyNjR8MA&ixlib=rb-4.1.0&q=85"
      />

      <section className="bg-[#eaf6fc] border-b border-[#1ca5dc]/20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
          <Sparkles size={18} className="text-[#1ca5dc]" />
          <p className="text-[#132b4d] font-medium">
            These reports show what EIG can deliver.{' '}
            <a href="#brief" className="text-[#1ca5dc] font-semibold hover:underline">Commission us</a>{' '}
            for bespoke research tailored to your business.
          </p>
        </div>
      </section>

      <section className="py-20 eig-graph">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="text-center mb-14 max-w-2xl mx-auto">
            <span className="eig-eyebrow justify-center">Report library</span>
            <h2 className="mt-5 font-heading font-extrabold text-3xl md:text-5xl text-[#132b4d]">
              Intelligence, <span className="text-gradient-eig">free to read</span>
            </h2>
            <p className="mt-6 text-slate-600">Previously sold at $399 each — now free. Read online in your browser or download the PDF.</p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reports.map((r, i) => (
              <Reveal key={r.id} delay={i * 60}>
                <div className="group bg-white rounded-2xl overflow-hidden ring-1 ring-slate-200/70 hover:shadow-2xl hover:shadow-[#132b4d]/10 transition-all duration-300 h-full flex flex-col">
                  <button onClick={() => setReading(r)} className="relative bg-[#0f2340] flex items-center justify-center h-64 overflow-hidden text-left">
                    <img src={r.cover} alt={r.title} className="h-full w-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500" />
                    <span className="absolute top-4 left-4 bg-[#1ca5dc] text-white text-xs font-heading font-semibold px-3 py-1 rounded-full uppercase tracking-wide">{r.tag}</span>
                    <span className="absolute top-4 right-4 bg-emerald-500 text-white text-xs font-heading font-bold px-3 py-1 rounded-full uppercase tracking-wide">Free</span>
                    <span className="absolute inset-0 flex items-center justify-center bg-[#0f2340]/0 group-hover:bg-[#0f2340]/40 transition-colors">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-2 bg-white text-[#132b4d] font-heading font-semibold px-5 py-2.5 rounded-full text-sm">
                        <BookOpen size={16} /> Read online
                      </span>
                    </span>
                  </button>
                  <div className="p-7 flex flex-col flex-1">
                    <h3 className="font-heading font-bold text-lg text-[#132b4d] leading-snug">{r.title}</h3>
                    {r.subtitle && <p className="text-sm text-[#1ca5dc] font-medium mt-1">{r.subtitle}</p>}
                    <p className="text-slate-600 leading-relaxed text-sm mt-3 flex-1">{r.summary}</p>

                    {r.highlights && r.highlights.length > 0 && (
                      <ul className="mt-4 space-y-2">
                        {r.highlights.slice(0, 3).map((h, k) => (
                          <li key={h} className="flex items-start gap-2 text-xs text-slate-600">
                            <Check size={14} className="text-[#1ca5dc] mt-0.5 shrink-0" />
                            <span>{h}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="flex items-center gap-2 text-xs text-slate-500 mt-5 mb-5">
                      <FileText size={15} className="text-[#1ca5dc]" /> {r.pages} pages · PDF{r.year ? ` · ${r.year}` : ''}
                    </div>
                    <div className="flex items-center gap-3 mt-auto">
                      <button onClick={() => setReading(r)} className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1ca5dc] text-white font-heading font-semibold px-4 py-2.5 rounded-full hover:bg-[#132b4d] transition-colors text-sm">
                        <BookOpen size={16} /> Read
                      </button>
                      <button onClick={() => download(r)} className="inline-flex items-center justify-center gap-2 border border-slate-300 text-[#132b4d] font-heading font-semibold px-4 py-2.5 rounded-full hover:border-[#1ca5dc] hover:text-[#1ca5dc] transition-colors text-sm">
                        <Download size={16} /> PDF
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* The "flash" — research brief */}
      <section id="brief" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="bg-[#0f2340] rounded-[28px] p-8 md:p-14 relative overflow-hidden">
              <div className="eig-blob w-[400px] h-[400px] bg-[#1ca5dc]/25 -top-24 -right-10" />
              <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-10 lg:gap-14 items-center">
                <div>
                  <span className="eig-eyebrow" style={{ color: '#5cc6ef' }}>Commission EIG</span>
                  <h3 className="mt-4 font-heading font-extrabold text-2xl md:text-[2.5rem] md:leading-[1.1] text-white">
                    Now you’ve seen what we can do — <span className="text-[#5cc6ef]">think what we could do for you…</span>
                  </h3>
                  <p className="mt-6 text-slate-300 leading-relaxed">
                    Detail the research you need undertaken, and we will assess whether it is something our
                    consultants can take forward. Every report in this library began exactly this way — with a
                    question that mattered to someone in the sector.
                  </p>
                </div>

                <form onSubmit={submitBrief} className="bg-white rounded-2xl p-6 md:p-8 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input value={brief.name} onChange={(e) => setBrief({ ...brief, name: e.target.value })} placeholder="Your name *" className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1ca5dc] focus:ring-2 focus:ring-[#1ca5dc]/20 transition" />
                    <input value={brief.email} onChange={(e) => setBrief({ ...brief, email: e.target.value })} type="email" placeholder="Your email *" className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1ca5dc] focus:ring-2 focus:ring-[#1ca5dc]/20 transition" />
                  </div>
                  <input value={brief.organisation} onChange={(e) => setBrief({ ...brief, organisation: e.target.value })} placeholder="Organisation" className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1ca5dc] focus:ring-2 focus:ring-[#1ca5dc]/20 transition" />
                  <textarea value={brief.details} onChange={(e) => setBrief({ ...brief, details: e.target.value })} rows={5} placeholder="Describe the research you need undertaken… *" className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1ca5dc] focus:ring-2 focus:ring-[#1ca5dc]/20 transition resize-none" />
                  <button type="submit" disabled={savingBrief} className="w-full inline-flex items-center justify-center gap-2 bg-[#1ca5dc] text-white font-heading font-semibold px-6 py-3.5 rounded-full hover:bg-[#132b4d] transition-colors disabled:opacity-70">
                    {savingBrief ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <>Submit your brief <ArrowRight size={18} /></>}
                  </button>
                  <p className="text-xs text-slate-400 text-center">We’ll assess your request and reply — no obligation.</p>
                </form>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* In-site reader */}
      {reading && (
        <div className="fixed inset-0 z-[60] bg-[#0f2340]/95 backdrop-blur-sm flex flex-col">
          <div className="flex items-center justify-between px-5 lg:px-8 py-4 border-b border-white/10">
            <div className="min-w-0">
              <h4 className="text-white font-heading font-bold truncate">{reading.title}</h4>
              {reading.subtitle && <p className="text-[#5cc6ef] text-sm truncate">{reading.subtitle}</p>}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button onClick={() => download(reading)} className="hidden sm:inline-flex items-center gap-2 bg-[#1ca5dc] text-white font-heading font-semibold px-5 py-2.5 rounded-full hover:bg-white hover:text-[#132b4d] transition-colors text-sm">
                <Download size={16} /> Download
              </button>
              <button onClick={() => setReading(null)} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors" aria-label="Close reader">
                <X size={22} />
              </button>
            </div>
          </div>
          <div className="flex-1 bg-slate-200 relative">
            <iframe title={reading.title} src={`${fileUrl(reading.id)}#view=FitH`} className="w-full h-full" />
            <a
              href={fileUrl(reading.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-[#132b4d] bg-white/90 px-4 py-2 rounded-full shadow hover:bg-white transition-colors"
            >
              Report not displaying? Open in a new tab
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
